import { range } from 'lodash-es'
import { computed, isRef, ref, shallowRef, toRef, watch } from 'vue'

/**
 * Unified composable for selectable records — replaces both the
 * `selectableRows` mixin and the `useSelectableObjects` composable.
 *
 * Selection is tracked by **object reference** (using a Set), making it
 * independent of sort order. When the source array is re-sorted with the
 * same objects, selection is preserved.
 *
 * @param {Ref<Array>|Array} source - reactive ref of records, or a plain array
 * @param {Object} [options]
 * @param {Array<number>} [options.initialSelection=[]] - indices to pre-select
 * @param {Ref<string>|'checkbox'|'select'|'radio'} [options.mode='checkbox'] - selection mode
 *   (accepts a Ref for dynamic switching):
 *   - 'checkbox': each click toggles that row; Shift extends range (multi-select)
 *   - 'select': click replaces the selection; Cmd/Ctrl adds/removes; Shift extends; additive flag toggles
 *   - 'radio': strictly one item — every click selects only that row; all modifiers ignored
 * @param {Object} [options.emit] - component emit function; when provided,
 *   auto-emits `'selected'` with selected objects on every change
 */
export function useSelectableRecords(source, options = {}) {
  const { initialSelection = [], mode: modeOption, emit } = options
  const modeRef = toRef(modeOption ?? 'checkbox')

  // shallowRef: prevents Vue from deep-proxying the Set, which would wrap
  // iterated values in reactive() and break object-identity comparisons.
  const selectedSet = shallowRef(new Set())

  const lastToggledIndex = ref(-1)

  function getSource() {
    return (isRef(source) ? source.value : source) || []
  }

  // Initialise selection from initial indices
  function initSelection(items, indices) {
    const set = new Set()
    for (const idx of indices) {
      if (idx >= 0 && idx < items.length) {
        set.add(items[idx])
      }
    }
    selectedSet.value = set
  }

  // Initialise
  const items = getSource()
  if (initialSelection.length) {
    initSelection(items, initialSelection)
  }

  // When source changes, prune selected objects that are no longer present
  if (isRef(source)) {
    watch(source, (newItems) => {
      const currentItems = newItems || []
      const currentSet = new Set(currentItems)
      const pruned = new Set()
      for (const obj of selectedSet.value) {
        if (currentSet.has(obj)) {
          pruned.add(obj)
        }
      }
      if (pruned.size !== selectedSet.value.size) {
        selectedSet.value = pruned
      }
      // Reset shift-click anchor — sort change invalidates the index
      lastToggledIndex.value = -1
    })
  }

  /**
   * Imperative init — for consumers that need to set records after
   * construction (e.g. the old mixin pattern).
   */
  function setRecords(items, selectedIndices = []) {
    selectedSet.value = new Set()
    if (selectedIndices.length) {
      initSelection(items, selectedIndices)
    }
    lastToggledIndex.value = -1
  }

  // --- Derived state ---

  const selectables = computed(() => {
    const items = getSource()
    return items.map((item) => ({
      selected: selectedSet.value.has(item),
      object: item,
    }))
  })

  const selectedIndices = computed(() => {
    const items = getSource()
    const indices = []
    for (let i = 0; i < items.length; i++) {
      if (selectedSet.value.has(items[i])) indices.push(i)
    }
    return indices
  })

  const selectedObjects = computed(() => {
    const items = getSource()
    const objs = []
    for (const item of items) {
      if (selectedSet.value.has(item)) objs.push(item)
    }
    return objs
  })

  const selectedCount = computed(() => selectedSet.value.size)
  const maxCount = computed(() => getSource().length)
  const allSelected = computed(
    () => maxCount.value > 0 && selectedCount.value === maxCount.value,
  )

  // --- Selection operations ---

  function isSelected(index) {
    const items = getSource()
    return (
      index >= 0 && index < items.length && selectedSet.value.has(items[index])
    )
  }

  function select(index) {
    const items = getSource()
    if (index >= 0 && index < items.length) {
      if (!selectedSet.value.has(items[index])) {
        const newSet = new Set(selectedSet.value)
        newSet.add(items[index])
        selectedSet.value = newSet
      }
    }
  }

  function deselect(index) {
    const items = getSource()
    if (index >= 0 && index < items.length) {
      if (selectedSet.value.has(items[index])) {
        const newSet = new Set(selectedSet.value)
        newSet.delete(items[index])
        selectedSet.value = newSet
      }
    }
  }

  function selectMultiple(indices) {
    const items = getSource()
    const newSet = new Set(selectedSet.value)
    let changed = false
    for (const i of indices) {
      if (i >= 0 && i < items.length && !newSet.has(items[i])) {
        newSet.add(items[i])
        changed = true
      }
    }
    if (changed) selectedSet.value = newSet
  }

  function selectRange(fromIdx, toIdx) {
    const min = Math.min(fromIdx, toIdx)
    const max = Math.max(fromIdx, toIdx)
    selectMultiple(range(min, max + 1))
  }

  function deselectRange(fromIdx, toIdx) {
    const items = getSource()
    const min = Math.min(fromIdx, toIdx)
    const max = Math.max(fromIdx, toIdx)
    const newSet = new Set(selectedSet.value)
    let changed = false
    for (let i = min; i <= max; i++) {
      if (i >= 0 && i < items.length && newSet.has(items[i])) {
        newSet.delete(items[i])
        changed = true
      }
    }
    if (changed) selectedSet.value = newSet
  }

  function selectAll() {
    const items = getSource()
    selectedSet.value = new Set(items)
  }

  function deselectAll() {
    if (selectedSet.value.size > 0) {
      selectedSet.value = new Set()
    }
  }

  // Replace the entire selection with exactly the given indices in a single
  // mutation, so consumers only see one `'selected'` change (no transient
  // empty state, as a deselectAll()+selectMultiple() pair would produce).
  function setSelection(indices) {
    const items = getSource()
    const newSet = new Set()
    for (const i of indices) {
      if (i >= 0 && i < items.length) newSet.add(items[i])
    }
    selectedSet.value = newSet
  }

  /**
   * @param {number} index
   * @param {boolean|Object} modifiers - either a boolean (shiftKey) for
   *   backwards compat, or { shiftKey, metaKey, ctrlKey }
   * @param {Object} [opts] - additional options
   * @param {boolean} [opts.additive] - force additive toggle (bypasses select-mode replace)
   */
  function toggleSelect(index, modifiers, opts) {
    const shift =
      typeof modifiers === 'boolean' ? modifiers : modifiers?.shiftKey === true
    const cmdOrCtrl =
      typeof modifiers === 'boolean'
        ? false
        : modifiers?.metaKey === true || modifiers?.ctrlKey === true
    const additive = opts?.additive === true

    if (modeRef.value === 'radio') {
      deselectAll()
      select(index)
    } else if (shift && lastToggledIndex.value >= 0) {
      if (!isSelected(index)) selectRange(lastToggledIndex.value, index)
      else deselectRange(lastToggledIndex.value, index)
    } else if (modeRef.value === 'select' && !additive) {
      if (cmdOrCtrl) {
        if (!isSelected(index)) select(index)
        else deselect(index)
      } else if (isSelected(index) && selectedCount.value === 1) {
        // Clicking the sole selected row toggles it off.
        deselect(index)
      } else {
        deselectAll()
        select(index)
      }
    } else {
      if (!isSelected(index)) select(index)
      else deselect(index)
    }
    lastToggledIndex.value = index
  }

  function toggleSelectAll() {
    if (modeRef.value === 'radio') return
    if (allSelected.value) {
      deselectAll()
    } else {
      selectAll()
    }
    lastToggledIndex.value = -1
  }

  // --- Auto-emit ---

  if (emit) {
    watch(selectedObjects, (objs) => emit('selected', objs))
  }

  return {
    selectables,
    selectedIndices,
    selectedObjects,
    selectedCount,
    maxCount,
    allSelected,
    lastToggledIndex,
    // Methods
    setRecords,
    isSelected,
    select,
    deselect,
    selectMultiple,
    selectRange,
    deselectRange,
    selectAll,
    deselectAll,
    setSelection,
    toggleSelect,
    toggleSelectAll,
  }
}

export const SELECTION_MODES = ['checkbox', 'select', 'radio']
